/**
 * The one wire between the tax engine's snapshots and the rest of the app.
 *
 * WHY THIS EXISTS
 * Tax snapshots live in their own IDB store and never enter S — they are an engine artefact,
 * they are large, and the cloud blob is not their home. That separation is right, and on
 * 2026-08-10 it cost real money: a ₺93,669 refund reached the bank, the tax page printed
 * REFUND PAID with the amount, the date and the EFT reference, and every cash, surplus,
 * free-TRY and capacity figure in the app was that much short. Nothing was broken. Nothing
 * could see it.
 *
 * So exactly three facts cross the boundary — which year, how much, on what date — and they
 * cross through here rather than through an import of the tax module. Consumers that only
 * need the facts (the watchdog, the notification bell) do not have to pull in the tax page,
 * the cash-flow module and the render loop behind it to ask a question about money.
 *
 * NO IMPORTS, NO STATE WRITES, NO NETWORK. Same contract as integrity.js and capital.js.
 *
 * NULL IS NOT EMPTY. `null` means nobody has read the snapshots yet; `[]` means the engine
 * knows of no paid refund. Collapsing the two would report a refund sitting unbooked in the
 * bank as a clean check, which is the exact failure this feeds.
 */

let _paid = null;

/** Called by the tax module every time its snapshot set changes. */
export function publishTaxRefunds(list) {
  _paid = Array.isArray(list) ? list.slice() : null;
}

/** @returns {Array|null} paid refunds, or null if the snapshots have not been read. */
export function taxRefundsPaid() {
  return _paid;
}

/** Test seam — drops the published set back to "nobody has looked". */
export function _resetTaxRefunds() {
  _paid = null;
  _seen = null;
}

/**
 * ── WHEN DID THE ENGINE LAST RUN? (2026-08-17) ──────────────────────────────────────────────
 *
 * A fourth fact, and it crosses for the same reason the first three do. `taxengine.py` runs
 * unattended — launchd, on every document drop and nightly at 21:00 — and it fails **loudly to
 * its own log and silently to everything else**: `autorun.sh` exits when `pdftotext` is missing,
 * which the log shows has actually happened, and `results.json` simply stays as it was.
 *
 * The Tax page does mark a snapshot STALE past 35 days. The **Integrity page** — the one place
 * he opens to ask "is anything wrong" — had no tax-freshness check at all, so a dead agent was
 * invisible exactly where he would go looking for it.
 *
 * Same NULL IS NOT EMPTY rule as above, and for the same reason: `null` means nobody has read
 * the snapshots, `[]` means there are none. A device with no tax snapshots must not report a
 * stale engine, and an unread store must not report a fresh one.
 */
let _seen = null;

/** Called by the tax module with `[{ year, generated }]` — the engine-run date per snapshot. */
export function publishTaxSnapshots(list) {
  _seen = Array.isArray(list) ? list.slice() : null;
}

/** @returns {Array|null} `[{ year, generated }]`, or null if the snapshots have not been read. */
export function taxSnapshotsSeen() {
  return _seen;
}
